import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Calendar, Users, Phone, Mail, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DatePicker } from '../components/DatePicker';
import { rooms, hotelInfo } from '../data/mock';
import { toast } from 'sonner';

const Booking = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    checkIn: null,
    checkOut: null,
    roomType: '',
    guests: '2',
    specialRequests: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.checkIn || !formData.checkOut) {
      toast.error('Please select check-in and check-out dates');
      return;
    }
    
    // Validate dates
    if (formData.checkOut <= formData.checkIn) {
      toast.error('Check-out date must be after check-in date');
      return;
    }
    
    try {
      // Send to backend API
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      
      const response = await fetch(`${BACKEND_URL}/api/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          check_in: formData.checkIn.toISOString().split('T')[0],
          check_out: formData.checkOut.toISOString().split('T')[0],
          room_type: formData.roomType || '',
          guests: parseInt(formData.guests) || 2,
          special_requests: formData.specialRequests || '',
        }),
      });
      
      // Always send WhatsApp message with all details
      // Prepare WhatsApp message with all user details
      const whatsappMessage = `*New Booking Request*
    
👤 *Name:* ${formData.name || 'Not provided'}
📧 *Email:* ${formData.email || 'Not provided'}
📞 *Phone:* ${formData.phone || 'Not provided'}
📅 *Check-in Date:* ${formData.checkIn ? formData.checkIn.toLocaleDateString('en-IN', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }) : 'Not selected'}
📅 *Check-out Date:* ${formData.checkOut ? formData.checkOut.toLocaleDateString('en-IN', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }) : 'Not selected'}
🏨 *Room Type:* ${formData.roomType || 'Not selected'}
👥 *Number of Guests:* ${formData.guests || '2'}
${formData.specialRequests ? `💬 *Special Requests:*\n${formData.specialRequests}` : ''}

Please confirm availability and pricing.`;
      
      // Send to WhatsApp number 8941000583
      const whatsappNumber = '918941000583';
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;
      
      // Open WhatsApp
      window.open(whatsappUrl, '_blank');
      
      if (response.ok) {
        toast.success('Booking request submitted successfully! Opening WhatsApp...');
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          checkIn: null,
          checkOut: null,
          roomType: '',
          guests: '2',
          specialRequests: '',
        });
      } else {
        toast.success('Opening WhatsApp with your details...');
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('An error occurred. Please try again or contact us directly.');
    }
  };

  return (
    <>
      <Helmet>
        <title>{`Online Booking | Hotel Suvidha Deluxe Haridwar`}</title>
        <meta name="description" content="Book your stay online at Hotel Suvidha Deluxe Haridwar for best prices and comfort." />
      </Helmet>
    <div className="min-h-screen pt-32 pb-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url(/images/reception.webp)', backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 font-serif text-gold-400">Book Your Stay</h1>
          <p className="text-xl text-gold-400 max-w-3xl mx-auto leading-relaxed">
            Reserve your perfect room and experience the best of Haridwar hospitality
          </p>
        </div>
      </section>

      {/* Booking Form */}
      <section className="container mx-auto px-4 -mt-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <Card className="border-0 shadow-2xl">
              <CardContent className="p-8">
                <h2 className="text-3xl font-bold text-navy-900 mb-6 font-serif">Reservation Details</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Personal Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-navy-900 mb-4">Personal Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="John Doe"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="john@example.com"
                          required
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="+91 1234567890"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Booking Details */}
                  <div>
                    <h3 className="text-lg font-semibold text-navy-900 mb-4">Booking Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Check-In Date *</Label>
                        <DatePicker
                          date={formData.checkIn}
                          onDateChange={(date) => setFormData({ ...formData, checkIn: date })}
                          placeholder="Select check-in date"
                        />
                      </div>
                      <div>
                        <Label>Check-Out Date *</Label>
                        <DatePicker
                          date={formData.checkOut}
                          onDateChange={(date) => setFormData({ ...formData, checkOut: date })}
                          placeholder="Select check-out date"
                          minDate={formData.checkIn ? new Date(formData.checkIn.getTime() + 86400000) : new Date()}
                        />
                      </div>
                      <div>
                        <Label htmlFor="roomType">Room Type *</Label>
                        <Select value={formData.roomType} onValueChange={(value) => setFormData({ ...formData, roomType: value })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select room type" />
                          </SelectTrigger>
                          <SelectContent>
                            {rooms.map((room) => (
                              <SelectItem key={room.id} value={room.name}>
                                {room.name} - ₹{room.price}/night
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="guests">Number of Guests *</Label>
                        <Select value={formData.guests} onValueChange={(value) => setFormData({ ...formData, guests: value })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">1 Guest</SelectItem>
                            <SelectItem value="2">2 Guests</SelectItem>
                            <SelectItem value="3">3 Guests</SelectItem>
                            <SelectItem value="4">4 Guests</SelectItem>
                            <SelectItem value="5">5+ Guests</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Special Requests */}
                  <div>
                    <Label htmlFor="specialRequests">Special Requests (Optional)</Label>
                    <Textarea
                      id="specialRequests"
                      value={formData.specialRequests}
                      onChange={(e) => setFormData({ ...formData, specialRequests: e.target.value })}
                      placeholder="Any special requirements or preferences..."
                      rows={4}
                    />
                  </div>

                  <Button type="submit" size="lg" className="w-full bg-gold-600 hover:bg-gold-700 text-white text-lg py-6">
                    Submit Booking Request
                  </Button>

                  <p className="text-sm text-gray-500 text-center">
                    * Our team will contact you within 24 hours to confirm your booking
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Booking Info */}
            <Card className="border-0 shadow-xl">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-navy-900 mb-4">Booking Information</h3>
                <ul className="space-y-3 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="text-gold-600 mt-0.5 flex-shrink-0" size={18} />
                    <span>Instant booking confirmation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="text-gold-600 mt-0.5 flex-shrink-0" size={18} />
                    <span>Free cancellation up to 24 hours</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="text-gold-600 mt-0.5 flex-shrink-0" size={18} />
                    <span>Pay at hotel - no prepayment needed</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="text-gold-600 mt-0.5 flex-shrink-0" size={18} />
                    <span>Best price guarantee</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Check-in/out Times */}
            <Card className="border-0 shadow-xl">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-navy-900 mb-4">Hotel Policies</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Calendar className="text-gold-600" size={20} />
                    <div>
                      <p className="font-semibold text-navy-900">Check-in</p>
                      <p className="text-sm text-gray-600">From 12:00 PM</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar className="text-gold-600" size={20} />
                    <div>
                      <p className="font-semibold text-navy-900">Check-out</p>
                      <p className="text-sm text-gray-600">Until 11:00 AM</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Card */}
            <Card className="border-0 shadow-xl bg-navy-900 text-white">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-4">Need Help?</h3>
                <p className="text-gray-300 text-sm mb-4">
                  Our team is available 24/7 to assist you with your booking
                </p>
                <div className="space-y-3 text-sm">
                  <a href="tel:+911234567890" className="flex items-center gap-3 hover:text-gold-400 transition-colors">
                    <Phone size={18} />
                    <span>+91-894100583</span>
                  </a>
                  <a href="mailto:info@hotelsuvidhadeluxe.com" className="flex items-center gap-3 hover:text-gold-400 transition-colors">
                    <Mail size={18} />
                    <span>info@hotelsuvidhadeluxe.com</span>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Booking;
